import intersection from 'lodash-es/intersection'
import isEmpty from 'lodash-es/isEmpty'
import find from 'lodash-es/find'
import reverse from 'lodash-es/reverse'
import { ECPairInterface as ECPair, Network, Transaction as RawTransaction, TransactionBuilder } from 'bitcoinjs-lib'
import { fromWIF, fromPrivateKey } from 'bitcoinjs-lib/src/ecpair'

import {
  ILogger, IUTXOProvider, IUTXOTransaction, IKeypair, IBTCTransactionAddress, BTCAddress, IBTCUnspent,
  OneChainError,
  RPCNode, UTXOProvider,
} from '@one-chain/core'
import { IApiProvider, ChainSo, BlockCypher, BlockABC } from './api-providers'
import { getNetworkByChainId } from './helper'
import { Transaction } from './Transaction'
import { OmniTransaction } from './OmniTransaction'
import { HDWallet } from './HDWallet'

/**
 * BTC Provider
 *
 * @export
 * @class BTC
 * @extends {UTXOProvider}
 * @implements {IUTXOProvider}
 */
export class BTC extends UTXOProvider implements IUTXOProvider {
  protected _apiProvider: IApiProvider
  protected _wallets: { [address: string]: ECPair } = {}
  protected _network: Network

  get keypairs () {
    return this._keypairs
  }

  get network () {
    return this._network
  }

  constructor (rpcnode: RPCNode, keypairs: IKeypair[], logger: ILogger) {
    super('BTC', rpcnode, keypairs, logger)

    this.setRPCNode(rpcnode)
    this.setKeypairs(keypairs)
  }

  setKeypairs (keypairs: IKeypair[]) {
    this.logger.debug('BTC - BTC.setKeypairs')
    this.logger.trace('BTC - BTC.setKeypairs:', keypairs)

    try {
      this._keypairs = keypairs
      this._wallets = {}
      for (const keypair of keypairs) {
        // 同时兼容 WIF 和 privateKey 格式
        try {
          this._wallets[keypair.address] = fromWIF(keypair.privateKey, this._network)
        }
        catch (err) {
          this._wallets[keypair.address] = fromPrivateKey(Buffer.from(keypair.privateKey, 'hex'), { compressed: true, network: this._network })
        }
      }
    }
    catch (err) {
      this.logger.error('BTC - BTC.setKeypairs: ', err)
      throw OneChainError.fromCode(110)
    }
  }

  setRPCNode (rpcnode: RPCNode) {
    this.logger.debug('BTC - BTC.setRPCNode')
    this.logger.trace('BTC - BTC.setRPCNode:', rpcnode)

    this._rpcnode = rpcnode
    if (rpcnode.baseUrl.match(/chain\.so/)) {
      this._apiProvider = new ChainSo(rpcnode, this.logger)
    }
    else if (rpcnode.baseUrl.match(/blockcypher\.com/)) {
      this._apiProvider = new BlockCypher(rpcnode, this.logger)
    }
    else if (rpcnode.baseUrl.match(/abcwallet\.com/)) {
      this._apiProvider = new BlockABC(rpcnode, this.logger)
    }
    else {
      this.logger.error('BTC - setRPCNode: unspported api ' + rpcnode.baseUrl)
      throw OneChainError.fromCode(100)
    }

    this._network = getNetworkByChainId(rpcnode.chainId)
  }

  /**
   * 构造交易对象
   *
   * @param {IBTCTransactionAddress[]} froms
   * @param {IBTCTransactionAddress[]} tos
   * @param {string} changeAddress
   * @param {string[]} [memos=[]]
   * @returns {Promise<Transaction>}
   * @memberof BTC
   */
  async buildTransaction (
    froms: IBTCTransactionAddress[],
    tos: IBTCTransactionAddress[],
    changeAddress: string,
    memos: string[] = [],
  ): Promise<Transaction> {
    return this._buildTransaction(froms, tos, changeAddress, memos)
  }

  /**
   * 构造自动修正的交易
   *
   * @param {IBTCTransactionAddress[]} froms
   * @param {IBTCTransactionAddress[]} tos
   * @param {string} changeAddress
   * @param {string[]} [memos=[]]
   * @returns {Promise<Transaction>}
   * @memberof BTC
   */
  async buildAutoFixTransaction (
    froms: IBTCTransactionAddress[],
    tos: IBTCTransactionAddress[],
    changeAddress: string,
    memos: string[] = [],
  ): Promise<Transaction> {
    return this._buildTransaction(froms, tos, changeAddress, memos, true)
  }

  /**
   * 构造交易对象
   *
   * @param {IBTCTransactionAddress[]} froms
   * @param {IBTCTransactionAddress[]} tos
   * @param {string} changeAddress
   * @param {string[]} [memos=[]]
   * @param {boolean} [autoFix=false]
   * @returns
   * @memberof BTC
   */
  async _buildTransaction (
    froms: IBTCTransactionAddress[],
    tos: IBTCTransactionAddress[],
    changeAddress: string,
    memos: string[] = [],
    autoFix = false
  ) {
    let wallets = null

    // 如果钱包不为空，就要检查其中密钥是否够用
    if (!isEmpty(this._wallets)) {
      const ret = intersection(froms.map(from => from.address), Object.keys(this._wallets))
      // 如果转出地址和钱包中地址不一致，提示错误
      if (ret.length !== froms.length) {
        throw OneChainError.fromCode(111)
      }

      wallets = this._wallets
    }

    const transaction = new Transaction(froms, tos, changeAddress, memos, autoFix)
    await transaction.init(this._apiProvider, this.logger, this._network, wallets)
    return transaction
  }

  /**
   * 构造 omni 交易
   *
   * @param {BTCAddress} from
   * @param {BTCAddress} to
   * @param {number} value
   * @returns
   * @memberof BTC
   */
  async buildOmniTransaction (from: BTCAddress, to: BTCAddress, value: number, propertyId: number = 31) {
    let wallets = null

    // 如果钱包不为空，就要检查其中密钥是否够用
    if (!isEmpty(this._wallets)) {
      // 如果转出地址和钱包中地址不一致，提示错误
      if (!this._wallets.hasOwnProperty(from)) {
        throw OneChainError.fromCode(111)
      }

      wallets = this._wallets
    }

    const transaction = new OmniTransaction(from, to, value, propertyId)
    await transaction.init(this._apiProvider, this.logger, this._network, wallets)
    return transaction
  }

  sign (rawTransaction: string, unspents: IBTCUnspent[]) {
    let trx = RawTransaction.fromHex(rawTransaction)
    const builder = TransactionBuilder.fromTransaction(trx, this._network)
    trx.ins.forEach((input, index) => {
      const txId = reverse(input.hash).toString('hex')
      const unspent = find(unspents, { txId: txId, vout: input.index })
      if (!this._wallets.hasOwnProperty(unspent.address)) {
        throw OneChainError.fromCode(117)
      }
      const ecpair = this._wallets[unspent.address]
      builder.sign(index, ecpair)
    })
    trx = builder.build()
    return trx.toHex()
  }

  /**
   * 推送交易
   *
   * @param {IUTXOTransaction} transaction
   * @returns {Promise<string>}
   * @memberof BTC
   */
  async pushTransaction (transaction: IUTXOTransaction): Promise<string> {
    const ret = await this._apiProvider.pushTransaction(transaction.toHex())
    return ret
  }

  /**
   * 通过助记词创建 HDWallet
   *
   * @param {string} mnemonic
   * @param {boolean} [needSync=true]
   * @returns
   * @memberof BTC
   */
  async hdwalletFromMnemonic (mnemonic: string, needSync = true) {
    const hdwallet = await HDWallet.fromMnemonic(mnemonic, this.rpcnode, this._apiProvider, needSync, this.logger)
    this.setKeypairs(hdwallet.getKeypairs())
    return hdwallet
  }

  /**
   * 通过 json 创建 HDWallet
   *
   * @param {*} json
   * @returns
   * @memberof BTC
   */
  async hdwalletFromJSON (json: any) {
    const hdwallet = await HDWallet.fromJSON(json, this.rpcnode, this._apiProvider, this.logger)
    this.setKeypairs(hdwallet.getKeypairs())
    return hdwallet
  }
}

export default BTC
